import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import type { OrderRequestRow } from '#shared/types';
import { Card } from '#client/components/ui/Card';
import { DataTable } from '#client/components/ui/DataTable';
import { EmptyState } from '#client/components/ui/EmptyState';
import { Page } from '#client/components/ui/Page';
import { Pagination } from '#client/components/ui/Pagination';
import { SearchFilter } from '#client/components/ui/SearchFilter';
import { Segmented } from '#client/components/ui/Segmented';
import { useResourceList } from '#client/hooks/useResourceList';
import { useUrlState } from '#client/hooks/useUrlState';
import { RESOURCE, routes } from '#client/lib/routes';
import { orderRequestColumns } from './order-request-columns';
import { ButtonLink } from '#client/components/ui/ButtonLink';

const STATUS_FILTERS = [
  { value: 'pending', label: 'Waiting' },
  { value: 'registered', label: 'Registered' },
  { value: 'declined', label: 'Set aside' },
  { value: 'all', label: 'Everything' },
];

/**
 * Tests a doctor has asked for, before anybody is a patient.
 *
 * Defaults to what is waiting, oldest first: an inbox is a queue, and the
 * request that has been sitting longest is the one to deal with. That ordering
 * is the server's — see `findAll`.
 */
export function OrderRequestListPage() {
  const navigate = useNavigate();
  const { params, setParam } = useUrlState();
  const status = params.get('status') ?? 'pending';

  const extraQuery = useMemo(() => (status === 'all' ? {} : { status }), [status]);
  const requests = useResourceList<OrderRequestRow>({
    path: RESOURCE.orderRequests,
    extraQuery,
  });

  return (
    <Page
      title="Test requests"
      subtitle="What doctors have asked for, before anybody is registered"
      primaryAction={
        <ButtonLink variant="primary" size="md" to={routes.orderRequestNew}>
          Take a request
        </ButtonLink>
      }
    >
      <Card flush>
        <div className="c-import-filter">
          <Segmented
            label="Show"
            options={STATUS_FILTERS}
            value={status}
            onChange={(choice) => setParam('status', choice === 'pending' ? undefined : choice)}
          />
          <SearchFilter
            value={requests.search}
            onChange={requests.setSearch}
            placeholder="Name, reference or doctor"
          />
        </div>

        <DataTable
          columns={orderRequestColumns}
          rows={requests.items}
          rowKey={(row) => row.id}
          loading={requests.loading}
          skeletonRows={requests.limit}
          caption="Test requests"
          onRowClick={(row) => void navigate(routes.orderRequest(row.id))}
          emptyState={
            <EmptyState
              title={status === 'pending' ? 'Nothing waiting' : 'Nothing here'}
              body="Requests taken off a fax, a call or a WhatsApp arrive here before they become a visit."
              tone={status === 'pending' ? 'positive' : 'neutral'}
            />
          }
        />

        <Pagination
          page={requests.page}
          limit={requests.limit}
          count={requests.count}
          onPageChange={requests.setPage}
        />
      </Card>
    </Page>
  );
}
